import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import i18n from '../../i18n';
// Chart option objects are built in plain helper functions and default
// parameters, where React hooks cannot be called — so translate through the
// i18n instance directly. Charts re-render when their page re-renders on a
// language change, so labels stay in sync.
const t = (key, opts) => i18n.t(key, opts);

const MONTHS_SHORT = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];

function firstOfMonth(d) {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addMonths(d, n) {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
}
function monthIndexDiff(a, b) {
  // months from a -> b (a,b are first-of-month)
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
}

const FCRBubbleChart = ({
  data,
  mode = "batch", // "batch" | "time"
  toDate,         // optional: end anchor for rolling window (time mode); defaults to today
  title = t('charts:titles.fcrByMonth'),
}) => {
  // ---- Process & bucket, supporting both modes
  const processedData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return {
        poorData: [],
        averageData: [],
        goodData: [],
        xAxisLabels: [],
        xAxisName: mode === "time" ? "Months" : "Batch Start Month",
      };
    }

    const poorData = [];
    const averageData = [];
    const goodData = [];
    let xAxisLabels = [];
    let xAxisName = "Batch Start Month";

    if (mode === "time") {
      // Rolling 12 months ending at toDate (or today)
      const end = toDate ? new Date(toDate) : new Date();
      const endM = firstOfMonth(end);
      const startM = addMonths(endM, -11);

      // Month labels (always 12)
      xAxisLabels = Array.from({ length: 12 }, (_, i) => {
        const d = addMonths(startM, i);
        return MONTHS_SHORT[d.getMonth()];
      });

      // Axis name like "Months (2024–2025)" or "Months (2025)"
      const startYear = startM.getFullYear();
      const endYear = endM.getFullYear();
      xAxisName = `Months (${startYear}${startYear === endYear ? "" : `–${endYear}`})`;

      // Map batches into month slots 0..11 (by startDate's month)
      data.forEach((item) => {
        const fcrValueRaw = Number(item.fcr);
        const fcrValue = Number.isFinite(fcrValueRaw) ? fcrValueRaw : 0;

        const s = new Date(item.startDate);
        const sM = firstOfMonth(s);
        const idx = monthIndexDiff(startM, sM); // 0..11 if in window
        if (idx < 0 || idx > 11) return;

        // Bubble size: same formula as BatchComparison (PEF bubble chart)
        // Inverse FCR so lower FCR (better) = larger bubble, similar to PEF logic
        const invertedFCR = 3.0 - fcrValue; // Invert so lower FCR gets higher value
        const rawSize = Math.max(invertedFCR * 30, 10); // Scale similar to PEF/8
        const bubbleSize = Math.min(rawSize, 60); // Same max as PEF chart

        const point = [
          idx,                   // x-index (month slot)
          fcrValue,              // y: FCR
          bubbleSize,            // symbolSize
          item.batchName ?? "-", // label text
          item.batchID ?? "-",   // batch id
          item.avgBirdWeight,    // avg weight
          item.pef,              // pef
          item.livability,       // livability
          item.startDate,        // start date for tooltip
          item.farmName ?? "-",  // farm name
        ];

        if (fcrValue > 1.8) poorData.push(point);
        else if (fcrValue >= 1.6) averageData.push(point);
        else goodData.push(point);
      });
    } else {
      // mode === "batch" (per-batch labels)
      const sorted = [...data].sort(
        (a, b) => new Date(a.startDate) - new Date(b.startDate)
      );

      xAxisLabels = [];
      xAxisName = "Batch Start Month";

      sorted.forEach((item, index) => {
        const fcrValueRaw = Number(item.fcr);
        const fcrValue = Number.isFinite(fcrValueRaw) ? fcrValueRaw : 0;

        const startDate = new Date(item.startDate);
        const monthName = startDate.toLocaleDateString("en-US", { month: "short" });
        const year = startDate.getFullYear();
        xAxisLabels.push(`${monthName} ${year}`);

        // Bubble size: same formula as BatchComparison (PEF bubble chart)
        // Inverse FCR so lower FCR (better) = larger bubble, similar to PEF logic
        const invertedFCR = 3.0 - fcrValue; // Invert so lower FCR gets higher value
        const rawSize = Math.max(invertedFCR * 30, 10); // Scale similar to PEF/8
        const bubbleSize = Math.min(rawSize, 60); // Same max as PEF chart

        const point = [
          index,                 // x-index (sequence)
          fcrValue,              // y: FCR
          bubbleSize,            // symbolSize
          item.batchName ?? "-", // label text
          item.batchID ?? "-",   // batch id
          item.avgBirdWeight,    // avg weight
          item.pef,              // pef
          item.livability,       // livability
          item.startDate,        // start date for tooltip
          item.farmName ?? "-",  // farm name
        ];

        if (fcrValue > 1.8) poorData.push(point);
        else if (fcrValue >= 1.6) averageData.push(point);
        else goodData.push(point);
      });
    }

    return { poorData, averageData, goodData, xAxisLabels, xAxisName };
  }, [data, mode, toDate]);

  // ---- Batch name with FCR labels above bubbles (auto-hide when crowded; show on hover)
  const totalPoints =
    processedData.poorData.length +
    processedData.averageData.length +
    processedData.goodData.length;
  const showInlineLabels = totalPoints <= 20;

  const bubbleLabel = {
    show: showInlineLabels,
    position: "top",
    distance: 6,
    formatter: (params) => {
      const batchName = params?.data?.[3]; // Batch name at index 3
      const fcrValue = params?.data?.[1]; // FCR at index 1
      const fcr = fcrValue ? Number(fcrValue).toFixed(2) : "-";
      return batchName ? `${batchName} - ${fcr}` : "";
    },
    fontSize: 10,
    fontWeight: 500,
    color: "#444",
    backgroundColor: "rgba(255,255,255,0.85)",
    padding: [2, 4],
    borderRadius: 3,
    borderColor: "#ddd",
    borderWidth: 1,
  };

  const option = useMemo(() => {
    return {
      title: {
        text: title,
        left: "center",
        top: 20,
        textStyle: { fontSize: 16, fontWeight: "bold", color: "#333" },
      },
      tooltip: {
        trigger: "item",
        confine: true,
        borderWidth: 0,
        backgroundColor: "rgba(30,30,30,0.9)",
        textStyle: { color: "#fff" },
        formatter: (params) => {
          const [
            , // xIndex
            fcr,
            , // bubbleSize
            batchName,
            batchId,
            avgWeight,
            pef,
            livability,
            startDate,
            farmName,
          ] = params.data || [];
          const fmt = (v, d = 2) =>
            Number.isFinite(Number(v)) ? Number(v).toFixed(d) : "-";
          return `
            <div style="padding:8px;">
              <div style="font-weight:700;margin-bottom:4px;">${batchName ?? "-"}</div>
              <div>${t('charts:tooltip.farm')}: ${farmName ?? "-"}</div>
              <div>${t('charts:tooltip.batchId')}: ${batchId ?? "-"}</div>
              <div>${t('charts:tooltip.startDate')}: ${startDate ?? "-"}</div>
              <div style="color:${params.color};font-weight:bold;font-size:13px;">${t('charts:tooltip.fcr')}: ${fmt(fcr)}</div>
              <div>${t('charts:tooltip.avgBirdWeight')}: ${fmt(avgWeight)} kg</div>
              <div>${t('charts:tooltip.pef')}: ${fmt(pef)}</div>
              <div>${t('charts:tooltip.livability')}: ${fmt(livability, 0)}%</div>
            </div>
          `;
        },
      },
      legend: {
        data: [t('charts:legend.fcrPoor'), "1.6 ≤ FCR ≤ 1.8 (Average)", t('charts:legend.fcrGood')],
        top: 55,
        right: 20,
        orient: "vertical",
        textStyle: { fontSize: 12, color: "#666" },
        itemGap: 15,
        itemWidth: 12,
        itemHeight: 12,
      },
      grid: {
        left: 60,
        right: 200,
        top: 80,
        bottom: mode === "time" ? 110 : 80,
        containLabel: true,
      },
      dataZoom: mode === "time"
        ? [
            { type: "inside", xAxisIndex: 0, zoomOnMouseWheel: true },
            { type: "slider", xAxisIndex: 0, height: 18, bottom: 10 },
          ]
        : undefined,
      axisPointer: { type: "cross" },
      xAxis: {
        type: "category",
        data: processedData.xAxisLabels,
        name: processedData.xAxisName,
        nameLocation: "center",
        nameGap: 50,
        nameTextStyle: { fontSize: 14, fontWeight: "bold", color: "#666" },
        axisLine: { lineStyle: { color: "#ccc", width: 1 } },
        axisTick: { lineStyle: { color: "#ccc" } },
        axisLabel: {
          color: "#666",
          fontSize: 11,
          rotate: mode === "time" ? 0 : 45,
          margin: 15,
        },
        splitLine: { show: true, lineStyle: { color: "#f0f0f0", type: "solid", width: 1 } },
      },
      yAxis: {
        type: "value",
        name: t('charts:axis.fcrLong'),
        nameLocation: "center",
        nameGap: 50,
        nameTextStyle: { fontSize: 14, fontWeight: "bold", color: "#666" },
        min: 1.0,
        max: 2.5,
        axisLine: { lineStyle: { color: "#ccc", width: 1 } },
        axisTick: { lineStyle: { color: "#ccc" } },
        axisLabel: { color: "#666", fontSize: 11 },
        splitLine: { show: true, lineStyle: { color: "#f0f0f0", type: "solid", width: 1 } },
      },
      animationDuration: 600,
      animationEasing: "quadraticOut",
      series: [
        {
          name: t('charts:legend.fcrGood'),
          type: "scatter",
          data: processedData.goodData,
          symbolSize: (d) => d[2],
          label: bubbleLabel,
          labelLayout: { hideOverlap: true },
          itemStyle: {
            color: "#27ae60",
            opacity: 0.85,
            borderColor: "#1e8449",
            borderWidth: 1,
          },
          emphasis: { label: { show: true }, itemStyle: { opacity: 1, borderWidth: 2 } },
          markLine: {
            silent: true,
            z: 10,
            lineStyle: { type: "dashed", width: 2, opacity: 0.9 },
            label: { position: "end", formatter: "{b}", fontSize: 12, fontWeight: "bold" },
            data: [
              { yAxis: 1.6, lineStyle: { color: "#27ae60" }, label: { formatter: t('charts:common.good'), color: "#27ae60" } },
              { yAxis: 1.8, lineStyle: { color: "#faad14" }, label: { formatter: t('charts:common.average'), color: "#faad14" } },
            ],
          },
        },
        {
          name: "1.6 ≤ FCR ≤ 1.8 (Average)",
          type: "scatter",
          data: processedData.averageData,
          symbolSize: (d) => d[2],
          label: bubbleLabel,
          labelLayout: { hideOverlap: true },
          itemStyle: {
            color: "#faad14",
            opacity: 0.85,
            borderColor: "#f39c12",
            borderWidth: 1,
          },
          emphasis: { label: { show: true }, itemStyle: { opacity: 1, borderWidth: 2 } },
        },
        {
          name: t('charts:legend.fcrPoor'),
          type: "scatter",
          data: processedData.poorData,
          symbolSize: (d) => d[2],
          label: bubbleLabel,
          labelLayout: { hideOverlap: true },
          itemStyle: {
            color: "#e74c3c",
            opacity: 0.85,
            borderColor: "#c0392b",
            borderWidth: 1,
          },
          emphasis: { label: { show: true }, itemStyle: { opacity: 1, borderWidth: 2 } },
        },
      ],
    };
  }, [processedData, title, mode]);

  // ---- No data placeholder
  if (!data || data.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "500px",
          backgroundColor: "#f8f9fa",
          borderRadius: "6px",
          border: "1px solid #dee2e6",
        }}
      >
        <div style={{ textAlign: "center", maxWidth: "300px" }}>
          <div style={{ fontSize: "48px", marginBottom: "15px", color: "#dee2e6" }}>📈</div>
          <div style={{ fontSize: "18px", fontWeight: 600, marginBottom: 8, color: "#495057" }}>
            {t('charts:common.noDataAvailable')}
          </div>
          <div style={{ fontSize: 14, color: "#6c757d", lineHeight: 1.4 }}>
            {t('charts:common.applyFiltersFcr')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="Temp">
      <ReactECharts
        option={option}
        style={{ width: "100%", height: "500px" }}
        opts={{ renderer: "svg" }}
      />
    </div>
  );
};

export default FCRBubbleChart;
